/**
 * Scroll reveal — one IntersectionObserver for the whole page.
 * Fades elements up once as they enter the viewport, staggered (--i) among the
 * elements of the same parent that enter together. See _reveal.scss for what
 * gets revealed; inc/reveal.php adds html.has-reveal before paint.
 */

const AUTO = [
  '.entry-content > .wp-block-group:not(.no-reveal) > :not(.wp-block-group__inner-container)',
  '.entry-content > .wp-block-group:not(.no-reveal) > .wp-block-group__inner-container > *',
].join(',');
const MAX_STAGGER = 6;

export function initReveal(root = document) {
  window.__vsReveal = true;
  if (!document.documentElement.classList.contains('has-reveal')) return;

  const targets = [
    ...root.querySelectorAll('[data-reveal]'),
    ...[...root.querySelectorAll(AUTO)].filter((el) => !el.querySelector('[data-reveal]')),
  ];
  if (!targets.length) return;

  // Stagger per parent, in document order.
  const reveal = (entering) => {
    const byParent = new Map();
    entering.forEach((el) => {
      const list = byParent.get(el.parentElement) || [];
      list.push(el);
      byParent.set(el.parentElement, list);
    });
    byParent.forEach((list) => {
      list
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
        .forEach((el, i) => {
          el.style.setProperty('--i', Math.min(i, MAX_STAGGER));
          el.classList.add('is-in');
          io.unobserve(el);
          // Drop the delay afterwards so later hover transitions aren't delayed.
          el.addEventListener('transitionend', () => el.style.removeProperty('--i'), { once: true });
        });
    });
  };

  const io = new IntersectionObserver((entries) => {
    reveal(entries.filter((e) => e.isIntersecting).map((e) => e.target));
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

  // Already on screen at load (e.g. a row just above the fold): reveal now —
  // the threshold/bottom margin would otherwise keep it hidden until a scroll.
  const vh = window.innerHeight;
  const onScreen = targets.filter((el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < vh && r.height > 0;
  });
  reveal(onScreen);

  targets.filter((el) => !onScreen.includes(el)).forEach((el) => io.observe(el));
}
